import { useContext, useEffect, useRef, useState } from 'react';
import Quill, { Delta } from 'quill';
import 'quill/dist/quill.snow.css';
import { BlogContent } from '../../types';
import { BlogContext } from '../../BlogContext';
import ArticleSkeleton from '../Skeletons/ArticleSkeleton';
import { ReviewModal } from '../Modals/ReviewModal';
import { convertToMarkdown } from './EditPanel';
import LinkBlot from './LinkBlot';

interface BlogEditorProps {
  content: BlogContent;
  onChange?: (content: BlogContent) => void;
  isEditPanelOpen: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onQuillRef: (quill: Quill) => void;
  onActiveFormatsChange: (formats: {
    [key: string]: boolean | number | string | unknown;
  }) => void;
  onWordCountChange: (count: number) => void;
  onDiscardAll?: () => void;
}

export function BlogEditor({
  content,
  isEditPanelOpen,
  onQuillRef,
  onActiveFormatsChange,
  onWordCountChange,
  onDiscardAll,
}: BlogEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const { isGenerating, handleUpdateBlogContent } = useContext(BlogContext);
  const initialContentRef = useRef<string>('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTextData, setSelectedTextData] = useState<{
    text: string;
    position: { x: number; y: number };
  }>({
    text: '',
    position: { x: 0, y: 0 },
  });
  const [lastSelection, setLastSelection] = useState<any>(null);
  const ignoreNextSelectionChange = useRef(false);
  const shouldRestoreSelection = useRef(false);

  const calculateWordCount = (text: string): number => {
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  function lineBreakMatcher() {
    const newDelta = new Delta();
    newDelta.insert({ break: '' });
    return newDelta;
  }

  const getSelectedText = (range: any) => {
    if (!quillRef.current || !range) {
      console.log('No quill instance or range');
      return '';
    }

    try {
      const windowSelection = window.getSelection();
      if (windowSelection && windowSelection.toString().trim()) {
        return windowSelection.toString();
      }

      return quillRef.current.getText(range.index, range.length) || '';
    } catch (error) {
      console.error('Error getting selected text:', error);
      return '';
    }
  };

  const getSelectionPosition = (range: any) => {
    if (!quillRef.current || !range) {
      console.log('No quill instance or range for position');
      return null;
    }

    try {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const selectionRange = selection.getRangeAt(0);
        const bbox = selectionRange.getBoundingClientRect();
        return {
          x: bbox.left + bbox.width / 2,
          y: bbox.top + window.scrollY,
        };
      }

      const bounds = quillRef.current.getBounds(range.index, range.length);
      const editorBounds = quillRef.current.container.getBoundingClientRect();

      return {
        x: editorBounds.left + (bounds?.left ?? 0) + (bounds?.width ?? 0) / 2,
        y: editorBounds.top + (bounds?.top ?? 0) + window.scrollY,
      };
    } catch (error) {
      console.error('Error getting selection position:', error);
      return null;
    }
  };

  const handleApplyToBlog = (newText: string) => {
    if (!quillRef.current || !lastSelection) return;

    try {
      const quill = quillRef.current;

      // Create a change delta that represents both the deletion and insertion
      const delta = new Delta()
        .retain(lastSelection.index)
        .delete(lastSelection.length)
        .insert(newText);

      // Apply the changes as a single operation that can be undone
      quill.updateContents(delta, 'user');

      // Update selection to the new text
      quill.setSelection(lastSelection.index, newText.length);
    } catch (error) {
      console.error('Error applying text to blog:', error);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleTextSelection = (_e: MouseEvent) => {
    if (!quillRef.current) return;

    const selection = window.getSelection();
    const selectedText = selection?.toString().trim();

    if (selectedText && selectedText.length > 0) {
      const range = selection?.getRangeAt(0);
      const bbox = range?.getBoundingClientRect();

      if (bbox) {
        setSelectedTextData({
          text: selectedText,
          position: {
            x: bbox.left + bbox.width / 2,
            y: bbox.top + window.scrollY,
          },
        });
        setIsModalOpen(true);
        ignoreNextSelectionChange.current = true;
      }
    }
  };

  const restoreSelection = () => {
    if (quillRef.current && lastSelection) {
      // Set a small timeout to ensure the DOM has settled after modal closing
      setTimeout(() => {
        quillRef.current?.setSelection(
          lastSelection.index,
          lastSelection.length
        );
      }, 10);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    shouldRestoreSelection.current = true;
    restoreSelection();
  };

  useEffect(() => {
    if (editorRef.current && !quillRef.current) {
      // Register custom blots
      Quill.register('formats/link', LinkBlot);

      quillRef.current = new Quill(editorRef.current, {
        modules: {
          toolbar: false,
          history: {
            delay: 400,
            maxStack: 500,
            userOnly: true,
            boundaries: true,
          },
          clipboard: {
            matchVisual: false,
            matchers: [['BR', lineBreakMatcher]],
          },
        },
        formats: [
          'bold',
          'italic',
          'underline',
          'strike',
          'link',
          'list',
          'header',
          'indent',
          'color',
          'background',
          'table',
          'check',
          'image',
        ],
        theme: 'snow',
      });

      onQuillRef(quillRef.current);

      const editor = quillRef.current.root;
      editor.addEventListener('mouseup', handleTextSelection);

      // Add discard-all event listener
      editor.addEventListener('discard-all', handleDiscardAll);

      quillRef.current.on('selection-change', (range) => {
        if (ignoreNextSelectionChange.current) {
          ignoreNextSelectionChange.current = false;
          return;
        }

        if (shouldRestoreSelection.current) {
          shouldRestoreSelection.current = false;
          return;
        }

        if (range) {
          const formats = quillRef.current?.getFormat(range) || {};
          onActiveFormatsChange(formats);
          setLastSelection(range);

          if (range.length > 0) {
            const selectedText = getSelectedText(range);
            const position = getSelectionPosition(range);

            if (selectedText && selectedText.trim() !== '' && position) {
              setSelectedTextData({
                text: selectedText,
                position: position,
              });
              setIsModalOpen(true);
            }
          }
        }
      });

      quillRef.current.on('text-change', () => {
        if (!quillRef.current) return;

        const text = quillRef.current.getText();
        const newWordCount = calculateWordCount(text);
        onWordCountChange(newWordCount);

        const selection = quillRef.current.getSelection();
        if (selection) {
          onActiveFormatsChange(
            quillRef.current.getFormat(selection) as {
              [key: string]: boolean | number | string | unknown;
            }
          );
        }
        const markDownContent = convertToMarkdown(
          quillRef?.current.root.innerHTML
        );

        //@ts-expect-error irreleventTypeIssue
        handleUpdateBlogContent((prevContent: BlogContent) => {
          return {
            ...prevContent,
            content: quillRef?.current?.root.innerHTML || '',
            markDownContent: markDownContent || '',
            wordCount: newWordCount || 0,
            settings: {
              ...prevContent.settings,
            },
          };
        });
      });
    }

    return () => {
      if (quillRef.current) {
        const editor = quillRef.current.root;
        editor.removeEventListener('mouseup', handleTextSelection);
        editor.removeEventListener('discard-all', handleDiscardAll);
      }
    };
  }, []);

  useEffect(() => {
    if (quillRef.current && content.content) {
      const currentContent = quillRef.current.root.innerHTML;
      if (currentContent !== content.content) {
        quillRef.current.root.innerHTML = content.content;
        // Store the initial content and state
        quillRef.current.root.setAttribute(
          'data-initial-content',
          content.content
        );
        quillRef.current.root.setAttribute(
          'data-initial-state',
          JSON.stringify({
            title: content.title,
            content: content.content,
            markDownContent: content.markDownContent,
            images: content.images,
            settings: content.settings,
            wordCount: content.wordCount,
          })
        );
        const text = quillRef.current.getText();
        const wordCount = calculateWordCount(text);
        onWordCountChange(wordCount);
      }
    }
  }, [content]);

  useEffect(() => {
    if (quillRef.current) {
      quillRef.current.enable(isEditPanelOpen);
    }
  }, [isEditPanelOpen]);

  // Add click handler for links
  useEffect(() => {
    const handleLinkClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'A') {
        e.preventDefault();
        const href = target.getAttribute('href');
        if (href) {
          // Ensure URL is absolute
          let absoluteUrl = href;
          if (!/^https?:\/\//i.test(href)) {
            absoluteUrl = 'https://' + href;
          }
          window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
        }
      }
    };

    // Use the current editor instance instead of querying the document
    if (quillRef.current) {
      const editorElement = quillRef.current.root;
      editorElement.addEventListener('click', handleLinkClick);

      return () => {
        editorElement.removeEventListener('click', handleLinkClick);
      };
    }

    return undefined;
  }, []);

  // Store initial content when editor is first mounted
  useEffect(() => {
    if (quillRef.current && content.content && !initialContentRef.current) {
      initialContentRef.current = content.content;
    }
  }, [content.content]);

  const handleDiscardAll = () => {
    if (quillRef.current && initialContentRef.current) {
      // Restore the initial content
      quillRef.current.root.innerHTML = initialContentRef.current;

      // Update the blog content state
      const text = quillRef.current.getText();
      const newWordCount = calculateWordCount(text);
      onWordCountChange(newWordCount);

      const markDownContent = convertToMarkdown(initialContentRef.current);

      handleUpdateBlogContent({
        ...content,
        content: initialContentRef.current,
        markDownContent: markDownContent,
        wordCount: newWordCount,
      });

      // Call the parent's discard handler if provided
      onDiscardAll?.();
    }
  };

  return (
    <div className="relative">
      {isGenerating ? (
        <div className="absolute w-full h-full bg-background-main z-[999]">
          <ArticleSkeleton />
        </div>
      ) : null}
      <div className="max-w-5xl  h-full rounded-lg mx-auto prose prose-lg relative flex flex-col">
        <div className="flex-grow overflow-auto">
          <div ref={editorRef} className="h-full" />
        </div>
      </div>
      <ReviewModal
        isOpen={isModalOpen && isEditPanelOpen}
        onClose={handleModalClose}
        selectedText={selectedTextData.text}
        setSelectedText={(text) =>
          setSelectedTextData({ ...selectedTextData, text })
        }
        onApplyToBlog={handleApplyToBlog}
      />
      <style>{`
        .ql-container.ql-snow {
          border: none;
          font-family: inherit;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        
        .ql-editor {
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          gap: 4px;
          
          overflow-y: auto;
          min-height: calc(100vh - 280px);
          max-height: calc(100vh - 280px);
          transition: opacity 0.3s ease;
        }

        .ql-editor.reverting {
          opacity: 0.5;
        }
        
        .ql-editor::-webkit-scrollbar {
          width: 8px;
        }
        
        .ql-editor::-webkit-scrollbar-track {
          background: background-main;
          border-radius: 4px;
        }
        
        .ql-editor::-webkit-scrollbar-thumb {
          background: primary;
          border-radius: 4px;
        }
        
        .ql-editor::-webkit-scrollbar-thumb:hover {
          background: primary;
        }

        .ql-editor h1 {
          font-size: 2.25rem;
          color: hsl(var(--text-primary));

          font-weight: bold;
        }
        
        .ql-editor h2 {
          font-size: 1.875rem;
          font-weight: bold;
          color: hsl(var(--text-primary));
        }
        
        .ql-editor h3 {
          font-size: 1.5rem;
          font-weight: bold;
          color: hsl(var(--text-primary));
        }
        
        .ql-editor h4 {
          font-size: 1.25rem;
          font-weight: bold;
          color: hsl(var(--text-primary));
        }
        
        .ql-editor h5 {
          font-size: 1.125rem;
          font-weight: bold;
          color: hsl(var(--text-primary));
        }
        
        .ql-editor h6 {
          font-size: 1rem;
          font-weight: bold;
          color: hsl(var(--text-primary));
        }
        
        .ql-editor p {
          font-size: 1.125rem;
          line-height: 1.75;
          color: hsl(var(--text-primary));
        }

        .ql-editor img {
          max-width: 100%;
          height: auto;
        }
        
        .ql-editor table {
          border-collapse: collapse;
          width: 100%;
        }
        
        .ql-editor td {
          border: 1px solid #cbd5e1;
          padding: 8px;
        }

        .ql-editor ul,
        .ql-editor ol {
          padding-left: 1.5em;
        }

        .ql-editor li {
          margin-bottom: 0.5em;
        }

        .ql-editor a {
          color: hsl(var(--text-primary));
          text-decoration: underline;
          cursor: pointer;
        }

        .ql-editor strong {
          color: hsl(var(--text-primary));
        }

        .ql-editor blockquote {
          border-left: 4px solid #e5e7eb;
          margin: 1.5em 0;
          padding-left: 1em;
          color: hsl(var(--text-primary));;
        }

        .ql-editor pre {
          background: #f3f4f6;
          padding: 1em;
          border-radius: 4px;
          overflow-x: auto;
        }

        .ql-editor code {
          background: #f3f4f6;
          padding: 0.2em 0.4em;
          border-radius: 3px;
          font-family: ui-monospace, monospace;
        }
      `}</style>
    </div>
  );
}

export default BlogEditor;
